"use client";

import Link from "next/link";
import { ArrowRight, Layers, Brain, Target, Rocket, ShieldAlert, Sliders, ChevronRight } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
} from "recharts";
import { Container, Section } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { Reveal } from "@/components/ui/reveal";
import { Counter } from "@/components/ui/counter";
import {
  HubHero, HubOverview, HubFeatures, HubUseCases, HubWhy, HubFAQ, HubCTA,
  CategoryHubComposer,
} from "@/components/ui/category-hub";
import {
  capabilitiesStats, capabilitiesList, capabilitiesFeatures, capabilitiesUseCases,
  capabilitiesWhy, capabilitiesFaq, capabilitiesCta, capabilityCategoryBreakdown,
} from "@/lib/data/capabilities-hub";

const CATEGORY_LABELS: Record<string, { label: string; icon: React.ReactNode; accent: string }> = {
  creation: { label: "Creation", icon: <Brain className="h-3.5 w-3.5" />, accent: "var(--color-crimson)" },
  deployment: { label: "Deployment", icon: <Rocket className="h-3.5 w-3.5" />, accent: "var(--color-purple)" },
  intelligence: { label: "Intelligence", icon: <Target className="h-3.5 w-3.5" />, accent: "var(--color-cyan)" },
  response: { label: "Response", icon: <ShieldAlert className="h-3.5 w-3.5" />, accent: "var(--color-amber)" },
  optimization: { label: "Optimization", icon: <Sliders className="h-3.5 w-3.5" />, accent: "var(--color-emerald)" },
};

/* ============================================================
   BESPOKE 1 — Capability matrix diagram
   A grid showing 20 capabilities with their category chips and
   connecting flow lines.
   ============================================================ */
function CapabilityMatrix() {
  return (
    <div className="relative w-full max-w-[560px] aspect-square mx-auto">
      <svg viewBox="0 0 520 520" className="w-full h-full" aria-hidden>
        <defs>
          <radialGradient id="ccore" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#06b6d4" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="260" cy="260" r="210" fill="url(#ccore)" />
        {/* 5 category rings */}
        {[80, 120, 160, 200, 240].map((r, i) => (
          <circle key={i} cx="260" cy="260" r={r} fill="none" stroke="rgb(255 255 255 / 0.05)" strokeDasharray={i % 2 === 0 ? "1 3" : "2 6"} />
        ))}
        {/* 20 capability nodes in 4 rings of 5 */}
        {capabilitiesList.map((c, i) => {
          const ring = Math.floor(i / 5);
          const posInRing = i % 5;
          const radii = [80, 130, 180, 230];
          const r = radii[ring] ?? 230;
          const startAngle = -Math.PI / 2;
          const stepAngle = (Math.PI * 2) / 5;
          const angle = startAngle + posInRing * stepAngle + (ring * 0.1);
          const ROUND = (n: number) => Math.round(n * 100) / 100;
          const x = ROUND(260 + Math.cos(angle) * r);
          const y = ROUND(260 + Math.sin(angle) * r);
          return (
            <g key={c.code}>
              <line
                x1="260"
                y1="260"
                x2={x}
                y2={y}
                stroke={`var(--color-${c.accent})`}
                strokeOpacity="0.2"
                strokeWidth="0.4"
                strokeDasharray="1 3"
              />
              <circle cx={x} cy={y} r="14" fill={`color-mix(in oklch, var(--color-${c.accent}) 25%, transparent)`} stroke={`var(--color-${c.accent})`} strokeWidth="0.6" />
              <text x={x} y={y + 2.5} textAnchor="middle" fontSize="6" fontWeight="800" fill="white" letterSpacing="0.3">
                {c.code}
              </text>
            </g>
          );
        })}
        {/* Center */}
        <g transform="translate(260, 260)">
          <circle r="50" fill="rgb(10 10 18 / 0.92)" />
          <circle r="44" fill="none" stroke="rgb(6 182 212 / 0.5)" strokeWidth="1" />
          <text x="0" y="-2" textAnchor="middle" fontSize="20" fontWeight="900" fill="white" letterSpacing="-0.5">
            20
          </text>
          <text x="0" y="14" textAnchor="middle" fontSize="5.5" fontWeight="700" fill="#06b6d4" letterSpacing="2">
            CAPABILITIES
          </text>
        </g>
      </svg>
      <div className="absolute top-2 right-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "0.5s" }}>
        <Layers className="h-3.5 w-3.5" style={{ color: "var(--color-cyan)" }} />
        <span className="text-xs font-semibold">5 Categories</span>
      </div>
      <div className="absolute bottom-2 left-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "1.5s" }}>
        <Brain className="h-3.5 w-3.5" style={{ color: "var(--color-purple)" }} />
        <span className="text-xs font-semibold">144-D Foundation</span>
      </div>
    </div>
  );
}

/* ============================================================
   BESPOKE 2 — Capability category distribution bar chart
   ============================================================ */
function CategoryBreakdownChart() {
  return (
    <div className="w-full h-[280px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={capabilityCategoryBreakdown} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: "rgb(255 255 255 / 0.7)", fontWeight: 600 }}
            axisLine={{ stroke: "rgb(255 255 255 / 0.1)" }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 10, fill: "rgb(255 255 255 / 0.5)" }}
            axisLine={false}
            tickLine={false}
            width={30}
          />
          <Tooltip
            cursor={{ fill: "rgb(255 255 255 / 0.05)" }}
            contentStyle={{
              background: "rgb(10 10 18 / 0.95)",
              border: "1px solid rgb(255 255 255 / 0.1)",
              borderRadius: "8px",
              fontSize: "12px",
            }}
            labelStyle={{ color: "white", fontWeight: 700 }}
            itemStyle={{ color: "rgb(255 255 255 / 0.7)" }}
          />
          <Bar dataKey="count" radius={[6, 6, 0, 0]}>
            {capabilityCategoryBreakdown.map((entry, i) => (
              <Cell key={`cell-${i}`} fill={entry.accent} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ============================================================
   BESPOKE 3 — Capability bento grid (grouped by category)
   ============================================================ */
function CapabilityBento() {
  const categories = ["creation", "deployment", "intelligence", "response", "optimization"] as const;
  return (
    <div className="space-y-10">
      {categories.map((cat) => {
        const items = capabilitiesList.filter((c) => c.category === cat);
        const catLabel = CATEGORY_LABELS[cat];
        return (
          <Reveal key={cat}>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs font-bold uppercase tracking-wider"
                  style={{ color: catLabel?.accent }}
                >
                  {catLabel?.icon}
                  {catLabel?.label}
                </span>
                <div className="text-xs text-[rgb(var(--fg-subtle))] font-semibold">
                  {items.length} {items.length === 1 ? "capability" : "capabilities"}
                </div>
                <div className="flex-1 h-px bg-gradient-to-r from-[rgb(var(--border)/0.2)] to-transparent" />
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="group block glass rounded-2xl p-5 h-full hover:scale-[1.02] transition-transform"
                    style={{
                      boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${item.accent}) 12%, transparent)`,
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className="grid place-items-center h-9 w-9 rounded-lg"
                        style={{
                          background: `color-mix(in oklch, var(--color-${item.accent}) 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${item.accent}) 35%, transparent)`,
                        }}
                      >
                        <span className="text-[10px] font-bold tracking-wider" style={{ color: `var(--color-${item.accent})` }}>
                          {item.code}
                        </span>
                      </span>
                      <ChevronRight className="h-3.5 w-3.5 text-[rgb(var(--fg-subtle))] group-hover:translate-x-1 transition-transform" />
                    </div>
                    <h3 className="text-sm font-bold">{item.title}</h3>
                    <p className="mt-1.5 text-xs text-[rgb(var(--fg-muted))] leading-relaxed line-clamp-2">
                      {item.description}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>
        );
      })}
    </div>
  );
}

/* ============================================================
   PAGE COMPOSER
   ============================================================ */
export function CapabilitiesHubPage() {
  return (
    <CategoryHubComposer
      overviewWrap="crimson"
      featuresWrap="purple"
      useCasesWrap="emerald"
      whyWrap="cyan"
      faqWrap="amber"
      ctaWrap="violet"
      hero={
        <section id="top" className="relative overflow-hidden pt-8 pb-16 sm:pt-12 sm:pb-24">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute top-10 left-1/4 h-72 w-72 rounded-full bg-[var(--color-cyan)] opacity-20 blur-[130px] animate-pulse-glow" />
            <div className="absolute top-32 right-1/4 h-80 w-80 rounded-full bg-[var(--color-purple)] opacity-18 blur-[140px] animate-pulse-glow" style={{ animationDelay: "2s" }} />
            <div className="absolute bottom-10 left-1/3 h-72 w-72 rounded-full bg-[var(--color-emerald)] opacity-15 blur-[140px] animate-pulse-glow" style={{ animationDelay: "4s" }} />
          </div>
          <Container size="wide">
            <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-6 items-center">
              <div className="flex flex-col items-start gap-6">
                <Reveal>
                  <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full glass">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--color-emerald)] opacity-75 animate-ping" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--color-emerald)]" />
                    </span>
                    <span className="eyebrow-tiny uppercase tracking-[0.28em]">20 Proprietary Capabilities</span>
                  </div>
                </Reveal>
                <Reveal delay={80}>
                  <h1 className="display-hero text-balance">
                    The Capability Stack{" "}
                    <span className="bg-gradient-to-br from-[var(--color-cyan)] via-[var(--color-purple)] to-[var(--color-crimson)] bg-clip-text text-transparent">
                      Behind Perception Engineering
                    </span>
                  </h1>
                </Reveal>
                <Reveal delay={160}>
                  <p className="text-[1.35rem] sm:text-[1.5rem] leading-[1.45] font-medium text-[rgb(var(--fg))] max-w-2xl">
                    20 composable capabilities across five categories. 144-dimensional emotional resonance. 95–98% organic authenticity. Built on 15+ years of operational refinement.
                  </p>
                </Reveal>
                <Reveal delay={220}>
                  <p className="text-body leading-relaxed text-[rgb(var(--fg-muted))] max-w-2xl">
                    Each capability is a composable operational primitive — usable independently, or orchestrated into bespoke sequences that match your engagement's strategic posture. Together, they form the most advanced perception engineering surface ever built.
                  </p>
                </Reveal>
                <Reveal delay={280}>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <Link
                      href="/capabilities/narrative-engineering"
                      className="btn-primary-gradient h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base shadow-2xl shadow-[var(--color-cyan)/30]"
                    >
                      Explore Narrative Engineering <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link
                      href="/capabilities/emotional-resonance"
                      className="btn-glass h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base"
                    >
                      144-D Resonance
                    </Link>
                  </div>
                </Reveal>
                <Reveal delay={360}>
                  <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-3xl w-full">
                    {capabilitiesStats.map((s) => (
                      <div
                        key={s.label}
                        className="glass rounded-2xl p-3 card-glow"
                        style={{ ["--glow" as string]: `var(--color-${s.accent})` }}
                      >
                        <div className="display-stat-sm" style={{ color: `var(--color-${s.accent})` }}>
                          <Counter to={s.to} suffix={s.suffix ?? ""} decimals={s.decimals ?? 0} />
                        </div>
                        <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mt-1.5 font-semibold uppercase tracking-wider">
                          {s.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </Reveal>
              </div>
              <Reveal delay={200} className="order-first lg:order-last">
                <CapabilityMatrix />
              </Reveal>
            </div>
          </Container>
        </section>
      }
      overview={
        <Section id="capabilities" className="relative">
          <Container>
            <SectionHeading
              eyebrow="Capability Matrix"
              title="Every Capability. Every Category. Every Composition."
              description="Browse the 20 proprietary capabilities organized by operational category — from creation and deployment to intelligence, response, and optimization."
              accent="crimson"
            />
            <CapabilityBento />
          </Container>
        </Section>
      }
      features={
        <>
          <Section id="composition" className="relative">
            <Container>
              <SectionHeading
                eyebrow="Capability Distribution"
                title="Five Categories, Twenty Primitives"
                description="The Perception X2 capability stack is organized into five operational categories. Each category is balanced to support the full perception engineering lifecycle."
                accent="purple"
              />
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div className="glass rounded-2xl p-6">
                  <CategoryBreakdownChart />
                </div>
                <div className="space-y-3">
                  {capabilityCategoryBreakdown.map((c) => (
                    <Reveal key={c.name}>
                      <div
                        className="glass rounded-2xl p-5 flex items-center justify-between"
                        style={{
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${c.accent} 18%, transparent)`,
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className="grid place-items-center h-10 w-10 rounded-lg"
                            style={{
                              background: `color-mix(in oklch, ${c.accent} 22%, transparent)`,
                              boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${c.accent} 40%, transparent)`,
                            }}
                          >
                            <span className="text-base font-bold font-mono-num" style={{ color: c.accent }}>
                              {c.count}
                            </span>
                          </span>
                          <div>
                            <h3 className="text-base font-bold">{c.name} Capabilities</h3>
                            <p className="text-xs text-[rgb(var(--fg-muted))] mt-0.5">
                              {c.count} of 20 primitives
                            </p>
                          </div>
                        </div>
                        <span className="text-2xl font-bold font-mono-num" style={{ color: c.accent }}>
                          {Math.round((c.count / 20) * 100)}%
                        </span>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            </Container>
          </Section>
          <Section id="features" className="relative mt-8">
            <Container>
              <HubFeatures
                eyebrow="Capability Properties"
                title="The Properties That Define the Stack"
                description="Eight properties shared by every Perception X2 capability — composability, real-time orchestration, continuous learning, and more."
                features={capabilitiesFeatures}
                accent="purple"
              />
            </Container>
          </Section>
        </>
      }
      useCases={
        <HubUseCases
          eyebrow="Capability Applications"
          title="Six Operational Compositions"
          description="Common patterns for composing the 20 capabilities into operational sequences that address specific engagement types."
          cases={capabilitiesUseCases}
          accent="emerald"
        />
      }
      why={
        <HubWhy
          eyebrow="Why These Capabilities"
          title="The Differentiators That Compound"
          items={capabilitiesWhy}
          accent="cyan"
        />
      }
      faq={
        <HubFAQ
          eyebrow="Capabilities FAQ"
          title="Frequently Asked Questions"
          items={capabilitiesFaq}
          accent="amber"
        />
      }
      cta={
        <HubCTA
          eyebrow={capabilitiesCta.eyebrow}
          title={capabilitiesCta.title}
          description={capabilitiesCta.description}
          primary={capabilitiesCta.primary}
          secondary={capabilitiesCta.secondary}
          trustItems={capabilitiesCta.trustItems}
          accent="violet"
        />
      }
    />
  );
}
